import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import {
  Heart, ArrowRight, ArrowLeft, CheckCircle, History,
  HelpCircle, MessageSquareHeart, BarChart3, Plus,
} from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { formatDateSafe } from "../lib/formatDate";

const EmotionRegisterPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [step, setStep] = useState(0);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState({
    situation: "",
    emotion_description: "",
    emotions: [],
    emotion_intensity: 7,
    socratic_q1: "",
    socratic_q2: "",
    socratic_q3: "",
    externalization_letter: "",
    adapted_response: "",
    new_emotion_intensity: 4,
  });
  const [customEmotion, setCustomEmotion] = useState("");

  const BASIC_EMOTIONS = [
    t("emotion.joy"), t("emotion.sadness"), t("emotion.anger"),
    t("emotion.fear"), t("emotion.disgust"), t("emotion.surprise"),
  ];
  const COMMON_EMOTIONS = [
    t("emotion.anxious"), t("emotion.frustrated"), t("emotion.ashamed"),
    t("emotion.guilty"), t("emotion.hopeless"), t("emotion.irritated"),
    t("emotion.worried"),
  ];

  const STEPS = [
    { icon: Heart, title: t("thought.situation"), desc: t("thought.situationPlaceholder") },
    { icon: HelpCircle, title: t("thought.socratic.title"), desc: t("thought.socratic.desc") },
    { icon: MessageSquareHeart, title: t("thought.externalize.title"), desc: t("thought.externalize.desc") },
    { icon: BarChart3, title: t("thought.newIntensity"), desc: t("thought.reeval.desc") },
  ];

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/emotion-register`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleEmotion = (emotion) => {
    setFormData((prev) => ({
      ...prev,
      emotions: prev.emotions.includes(emotion)
        ? prev.emotions.filter((e) => e !== emotion)
        : [...prev.emotions, emotion],
    }));
  };

  const addCustomEmotion = () => {
    const trimmed = customEmotion.trim();
    if (trimmed && !formData.emotions.includes(trimmed)) {
      setFormData((prev) => ({ ...prev, emotions: [...prev.emotions, trimmed] }));
      setCustomEmotion("");
    }
  };

  const handleSubmit = async () => {
    if (!formData.situation || formData.emotions.length === 0) {
      toast.error(t("thought.situation") + " & " + t("thought.emotions"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/emotion-register`, {
        situation: formData.situation,
        automatic_thoughts: formData.emotion_description,
        emotions: formData.emotions,
        emotion_intensity: formData.emotion_intensity,
        socratic_q1: formData.socratic_q1,
        socratic_q2: formData.socratic_q2,
        socratic_q3: formData.socratic_q3,
        externalization_letter: formData.externalization_letter,
        balanced_thought: formData.adapted_response,
        new_emotion_intensity: formData.new_emotion_intensity,
      }, { withCredentials: true });
      toast.success(t("thought.recorded"));
      if (user?.role === "patient") promptShare(res.data, "emotion_register");
      setFormData({
        situation: "", emotion_description: "", emotions: [], emotion_intensity: 7,
        socratic_q1: "", socratic_q2: "", socratic_q3: "",
        externalization_letter: "", adapted_response: "", new_emotion_intensity: 4,
      });
      setCustomEmotion("");
      setStep(0);
      fetchEntries();
    } catch (e) {
      toast.error(t("thought.errorRecording"));
    } finally { setSubmitting(false); }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.situation")}</label>
              <Textarea
                placeholder={t("thought.situationPlaceholder")}
                value={formData.situation}
                onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
                rows={3}
                data-testid="situation-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.automaticThoughts")}</label>
              <Textarea
                placeholder={t("thought.automaticThoughtsPlaceholder")}
                value={formData.emotion_description}
                onChange={(e) => setFormData({ ...formData, emotion_description: e.target.value })}
                rows={2}
                data-testid="emotion-desc-input"
              />
            </div>
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("thought.emotions")}</label>
              <p className="text-xs text-muted-foreground">{t("thought.basicEmotions")}</p>
              <div className="flex flex-wrap gap-2">
                {BASIC_EMOTIONS.map((em) => (
                  <button
                    key={em}
                    onClick={() => toggleEmotion(em)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      formData.emotions.includes(em)
                        ? "bg-primary text-white border-primary"
                        : "bg-background border-border hover:bg-muted"
                    }`}
                  >
                    {em}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{t("thought.commonEmotions")}</p>
              <div className="flex flex-wrap gap-2">
                {COMMON_EMOTIONS.map((em) => (
                  <button
                    key={em}
                    onClick={() => toggleEmotion(em)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      formData.emotions.includes(em)
                        ? "bg-primary text-white border-primary"
                        : "bg-background border-border hover:bg-muted"
                    }`}
                  >
                    {em}
                  </button>
                ))}
                {formData.emotions.filter(e => !BASIC_EMOTIONS.includes(e) && !COMMON_EMOTIONS.includes(e)).map(custom => (
                  <button key={custom} onClick={() => toggleEmotion(custom)}
                    className="px-3 py-1.5 rounded-full text-sm border transition-all bg-primary text-white border-primary">
                    {custom}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" placeholder={t("thought.customEmotion")} value={customEmotion}
                  onChange={(e) => setCustomEmotion(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomEmotion())}
                  className="flex-1 px-3 py-2 text-sm border rounded-full bg-background border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="custom-emotion-input" />
                <Button variant="outline" size="sm" onClick={addCustomEmotion} disabled={!customEmotion.trim()} className="rounded-full" data-testid="add-custom-emotion-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.intensity")}</label>
              <Slider
                value={[formData.emotion_intensity]}
                onValueChange={(v) => setFormData({ ...formData, emotion_intensity: v[0] })}
                min={1} max={10} step={1}
                data-testid="intensity-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1</span><span>{formData.emotion_intensity}/10</span><span>10</span>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.socratic.q1")}</label>
              <Textarea
                placeholder={t("thought.socratic.q1placeholder")}
                value={formData.socratic_q1}
                onChange={(e) => setFormData({ ...formData, socratic_q1: e.target.value })}
                rows={3}
                data-testid="socratic-q1"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.socratic.q2")}</label>
              <Textarea
                placeholder={t("thought.socratic.q2placeholder")}
                value={formData.socratic_q2}
                onChange={(e) => setFormData({ ...formData, socratic_q2: e.target.value })}
                rows={3}
                data-testid="socratic-q2"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.socratic.q3")}</label>
              <Textarea
                placeholder={t("thought.socratic.q3placeholder")}
                value={formData.socratic_q3}
                onChange={(e) => setFormData({ ...formData, socratic_q3: e.target.value })}
                rows={3}
                data-testid="socratic-q3"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-5">
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-sm font-medium text-primary">{t("thought.tipTitle")}</p>
              <p className="text-xs text-muted-foreground text-sm sm:text-base mt-1">{t("thought.tipText")}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.externalize.letter")}</label>
              <Textarea
                placeholder={t("thought.externalize.letterPlaceholder")}
                value={formData.externalization_letter}
                onChange={(e) => setFormData({ ...formData, externalization_letter: e.target.value })}
                rows={5}
                data-testid="externalize-letter"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("thought.externalize.response")}</label>
              <Textarea
                placeholder={t("thought.externalize.responsePlaceholder")}
                value={formData.adapted_response}
                onChange={(e) => setFormData({ ...formData, adapted_response: e.target.value })}
                rows={3}
                data-testid="adapted-response"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-5">
            <p className="text-sm text-muted-foreground">{t("thought.reeval.desc")}</p>
            <Slider
              value={[formData.new_emotion_intensity]}
              onValueChange={(v) => setFormData({ ...formData, new_emotion_intensity: v[0] })}
              min={1} max={10} step={1}
              data-testid="new-intensity-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1</span><span>{formData.new_emotion_intensity}/10</span><span>10</span>
            </div>
            {formData.new_emotion_intensity < formData.emotion_intensity && (
              <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-300">
                  {formData.emotion_intensity - formData.new_emotion_intensity} {t("thought.intensity").toLowerCase()} {t("common.less")}
                </p>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="emotion-register-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("thought.title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("thought.subtitle")}</p>
          </div>
          <Button
            variant={showHistory ? "default" : "outline"}
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            data-testid="toggle-history-btn"
          >
            <History className="w-4 h-4 mr-1" />
            {t("thought.newRecord")} ({entries.length})
          </Button>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.emotion_register} />

        {showHistory ? (
          <div className="space-y-4">
            {entries.length > 0 ? entries.map((entry) => (
              <Card key={entry.record_id} className="hover:shadow-sm transition-shadow">
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{entry.situation}</p>
                      {entry.automatic_thoughts && (
                        <p className="text-xs text-muted-foreground mt-1 italic">"{entry.automatic_thoughts}"</p>
                      )}
                    </div>
                    <div className="flex gap-2 items-center shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
                        {entry.emotion_intensity}/10
                      </span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
                        {entry.new_emotion_intensity}/10
                      </span>
                    </div>
                  </div>
                  {entry.emotions?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {entry.emotions.map((em) => (
                        <span key={em} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                          {em}
                        </span>
                      ))}
                    </div>
                  )}
                  {entry.balanced_thought && (
                    <p className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/50 p-2 rounded">
                      {entry.balanced_thought}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {formatDateSafe(entry.created_at)}
                  </p>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12">
                <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t("thought.noRecords")}</p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex gap-1.5 max-w-3xl mx-auto">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full cursor-pointer transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`}
                  onClick={() => setStep(i)}
                />
              ))}
            </div>

            <Card className="max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  {(() => { const SIcon = STEPS[step].icon; return <SIcon className="w-5 h-5" />; })()}
                  {STEPS[step].title}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{STEPS[step].desc}</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {renderStep()}

                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>
                    <ArrowLeft className="w-4 h-4 mr-1" />
                  </Button>
                  {step < STEPS.length - 1 ? (
                    <Button onClick={() => setStep(step + 1)} data-testid="next-step-btn">
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-primary hover:bg-primary/90"
                      data-testid="save-emotion-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {submitting ? t("exercise.saving") : t("common.save")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default EmotionRegisterPage;
